import { useEffect, useState } from "react";

import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { SadieSimulatorControls } from "@/components/tools/sadie-simulator/SadieSimulatorControls";
import { SadieSimulatorPromptChanges } from "@/components/tools/sadie-simulator/SadieSimulatorPromptChanges";
import { SadieSimulatorThread } from "@/components/tools/sadie-simulator/SadieSimulatorThread";
import { Button } from "@ui/button";

import { useCreateAddSadieSimulatorTapback } from "@/hooks/createAdds/useCreateAddSadieSimulatorTapback";
import { useCreateAddSadieSimulatorText } from "@/hooks/createAdds/useCreateAddSadieSimulatorText";
import { useGetSadieSimulatorTesters } from "@/hooks/queries/useGetSadieSimulatorTesters";
import { useGetSadieSimulatorThread } from "@/hooks/queries/useGetSadieSimulatorThread";
import { useUpdateSetSadieSimulatorPromptChanges } from "@/hooks/updateSets/useUpdateSetSadieSimulatorPromptChanges";
import { useUpdateSetSadieSimulatorRunning } from "@/hooks/updateSets/useUpdateSetSadieSimulatorRunning";
import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { SadieSimulatorPromptChange, SadieSimulatorTapback } from "@/types";

// Runs as a real tester, through the same code a tester's texts go through.
// Their texts come from this page and Sadie's come back to it, never to
// their phone.
export const ToolsSadieSimulatorPage = () => {
  /* Database CRUD Operations */
  // Read
  const {
    data: testers,
    isPending: isTestersPending,
    isError: isTestersError,
    refetch: refetchTesters,
  } = useGetSadieSimulatorTesters();
  // Update
  const {
    mutate: setRunning,
    isPending: isRunningPending,
    variables: runningChange,
  } = useUpdateSetSadieSimulatorRunning();
  const { mutateAsync: sendText, isPending: isSending } =
    useCreateAddSadieSimulatorText();
  const { mutate: sendTapback } = useCreateAddSadieSimulatorTapback();
  const { mutateAsync: savePromptChanges, isPending: isSavingPrompt } =
    useUpdateSetSadieSimulatorPromptChanges();

  /* React Hooks */
  const [pickedTalentId, setPickedTalentId] = useState<number | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const talentId =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    pickedTalentId || testers?.testers[0]?.talentId || null;
  const isStarting = isRunningPending && runningChange.isRunning;
  const isStopping = isRunningPending && !runningChange.isRunning;
  const { data: thread } = useGetSadieSimulatorThread(talentId, isStarting);
  const simulator = thread ? thread.simulator : null;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const promptChanges = simulator?.promptChanges || [];

  useSetTitle({ title: "Tools | Sadie Simulator" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SADIE_SIMULATOR);
  }, []);

  /* Handlers */
  const handleStart = () => {
    if (talentId === null) return;
    setRunning({ isRunning: true, talentId, model });
  };

  const handleStop = () => {
    if (talentId === null) return;
    setRunning({ isRunning: false, talentId });
  };

  const handleSend = async (body: string) => {
    if (talentId === null) return;
    await sendText({ talentId, body });
  };

  const handleTapback = (quoId: string, reaction: SadieSimulatorTapback) => {
    if (talentId === null) return;
    sendTapback({ talentId, quoId, reaction });
  };

  const handlePromptChangesSave = async (
    changes: SadieSimulatorPromptChange[],
  ) => {
    if (talentId === null) return;
    await savePromptChanges({ talentId, changes });
  };

  /* Render */
  const render = () => {
    if (isTestersPending) return <LoadingIndicator variant="medium" />;
    if (isTestersError) {
      return (
        <Row className="items-center justify-center gap-2 py-2">
          <ErrorText errorMessage="Could not load the testers." />
          <Button
            onClick={() => void refetchTesters()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        </Row>
      );
    }
    if (talentId === null) {
      return (
        <Center className="flex-1">
          <Text className="text-sm font-normal text-slate-500">
            No testers are set up. Add one to RELATIONSHIP_TESTERS.
          </Text>
        </Center>
      );
    }
    return (
      // Each pane scrolls on its own, so the thread's scroll-to-latest never
      // drags the whole page.
      <Row className="min-h-0 flex-1 overflow-hidden">
        <Column className="min-h-0 w-[360px] shrink-0 gap-6 overflow-y-auto border-r border-slate-200 bg-slate-50 p-4">
          <SadieSimulatorControls
            isStarting={isStarting}
            isStopping={isStopping}
            model={model}
            models={testers.models}
            onModelChange={setModel}
            onStart={handleStart}
            onStop={handleStop}
            onTesterChange={setPickedTalentId}
            selectedTalentId={talentId}
            sessions={thread ? thread.sessions : []}
            simulator={simulator}
            testers={testers.testers}
          />
          <SadieSimulatorPromptChanges
            changes={promptChanges}
            isSaving={isSavingPrompt}
            isSimulating={!!simulator}
            onSave={handlePromptChangesSave}
            prompt={thread ? thread.prompt : null}
          />
        </Column>
        <Column className="min-h-0 flex-1">
          <SadieSimulatorThread
            isSending={isSending}
            onSend={handleSend}
            onTapback={handleTapback}
            thread={thread}
          />
        </Column>
      </Row>
    );
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Sadie Simulator</TopbarTitle>
      </TopbarContainer>
      {render()}
    </Column>
  );
};
